"use client";

import { motion, useReducedMotion } from "framer-motion";

const seaNodes = [
  { label: "Cadet", y: 300 },
  { label: "Officer", y: 220 },
  { label: "Senior Officer", y: 150 },
  { label: "Command", y: 80 },
];

const shoreBranches = [
  { label: "Training", y: 40 },
  { label: "Crewing", y: 80 },
  { label: "Operations", y: 120 },
  { label: "Technical", y: 160 },
  { label: "Commercial", y: 200 },
  { label: "Compliance", y: 240 },
  { label: "Vetting", y: 280 },
  { label: "Port", y: 320 },
];

const mobileBranches = [
  { label: "Training", x: 160, y: 180 },
  { label: "Crewing", x: 300, y: 180 },
  { label: "Operations", x: 160, y: 260 },
  { label: "Technical", x: 300, y: 260 },
  { label: "Commercial", x: 160, y: 340 },
  { label: "Compliance", x: 300, y: 340 },
  { label: "Vetting", x: 160, y: 420 },
  { label: "Port", x: 300, y: 420 },
];

export default function CareerPathDiagram() {
  const reduced = useReducedMotion();

  const container = reduced
    ? { hidden: {}, visible: {} }
    : {
        hidden: {},
        visible: {
          transition: { staggerChildren: 0.08, delayChildren: 0.05 },
        },
      };

  const node = reduced
    ? { hidden: { opacity: 1, scale: 1 }, visible: { opacity: 1, scale: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.85 },
        visible: { opacity: 1, scale: 1 },
      };

  const line = reduced
    ? { hidden: { opacity: 0.65 }, visible: { opacity: 0.65 } }
    : { hidden: { opacity: 0 }, visible: { opacity: 0.65 } };

  return (
    <div
      className="relative w-full"
      role="img"
      aria-label="One route in, many routes out: a sea track from Cadet to Command fans out into Training, Crewing, Operations, Technical, Commercial, Compliance, Vetting and Port shore roles."
    >
      {/* Desktop fan */}
      <svg
        viewBox="0 0 760 360"
        className="hidden h-auto w-full sm:block"
        aria-hidden="true"
      >
        <defs>
          <filter id="careerGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <motion.g
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={container}
        >
          {/* Sea track connecting lines */}
          {seaNodes.slice(1).map((node, i) => (
            <motion.line
              key={`sea-line-${node.label}`}
              x1={80}
              y1={node.y}
              x2={80}
              y2={seaNodes[i].y}
              stroke="#00f3ff"
              strokeWidth={0.8}
              variants={line}
              transition={{ duration: 0.25 }}
            />
          ))}

          {/* Fan lines from Command apex to each shore branch */}
          {shoreBranches.map((branch) => (
            <motion.line
              key={`fan-${branch.label}`}
              x1={80}
              y1={80}
              x2={620}
              y2={branch.y}
              stroke="#00f3ff"
              strokeWidth={0.8}
              opacity={0.65}
              variants={line}
              transition={{ duration: 0.35 }}
            />
          ))}

          {/* Sea track nodes */}
          {seaNodes.map((s) => (
            <motion.g key={s.label} variants={node} transition={{ duration: 0.3 }}>
              <circle
                cx={80}
                cy={s.y}
                r={5}
                fill="none"
                stroke="#00f3ff"
                strokeWidth={1.5}
                filter="url(#careerGlow)"
              />
              <text
                x={100}
                y={s.y + 4}
                fill="#e8eaf2"
                fontSize={12}
                fontWeight={500}
              >
                {s.label}
              </text>
            </motion.g>
          ))}

          {/* Shore branch nodes */}
          {shoreBranches.map((branch) => (
            <motion.g
              key={branch.label}
              variants={node}
              transition={{ duration: 0.3 }}
            >
              <circle
                cx={620}
                cy={branch.y}
                r={5}
                fill="none"
                stroke="#00ff9d"
                strokeWidth={1.5}
                filter="url(#careerGlow)"
              />
              <text
                x={640}
                y={branch.y + 4}
                fill="#e8eaf2"
                fontSize={12}
                fontWeight={500}
              >
                {branch.label}
              </text>
            </motion.g>
          ))}
        </motion.g>
      </svg>

      {/* Mobile stacked layout */}
      <svg
        viewBox="0 0 360 520"
        className="block h-auto w-full sm:hidden"
        aria-hidden="true"
      >
        <defs>
          <filter
            id="careerGlowMobile"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <motion.g
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          variants={container}
        >
          {/* Sea track lines */}
          {seaNodes.slice(1).map((node, i) => (
            <motion.line
              key={`mobile-sea-line-${node.label}`}
              x1={60}
              y1={node.y}
              x2={60}
              y2={seaNodes[i].y}
              stroke="#00f3ff"
              strokeWidth={0.8}
              variants={line}
              transition={{ duration: 0.25 }}
            />
          ))}

          {/* Trunk from Command */}
          <motion.path
            d="M 60 130 L 140 130 L 140 440"
            fill="none"
            stroke="#00f3ff"
            strokeWidth={0.8}
            variants={line}
            transition={{ duration: 0.4 }}
          />

          {/* Horizontal connectors to each branch */}
          {mobileBranches.map((branch) => (
            <motion.line
              key={`mobile-conn-${branch.label}`}
              x1={140}
              y1={branch.y}
              x2={branch.x - 12}
              y2={branch.y}
              stroke="#00f3ff"
              strokeWidth={0.8}
              variants={line}
              transition={{ duration: 0.25 }}
            />
          ))}

          {/* Sea track nodes */}
          {seaNodes.map((s) => (
            <motion.g
              key={`mobile-sea-${s.label}`}
              variants={node}
              transition={{ duration: 0.3 }}
            >
              <circle
                cx={60}
                cy={s.y}
                r={5}
                fill="none"
                stroke="#00f3ff"
                strokeWidth={1.5}
                filter="url(#careerGlowMobile)"
              />
              <text
                x={80}
                y={s.y + 4}
                fill="#e8eaf2"
                fontSize={12}
                fontWeight={500}
              >
                {s.label}
              </text>
            </motion.g>
          ))}

          {/* Shore branch nodes */}
          {mobileBranches.map((branch) => (
            <motion.g
              key={`mobile-branch-${branch.label}`}
              variants={node}
              transition={{ duration: 0.3 }}
            >
              <circle
                cx={branch.x}
                cy={branch.y}
                r={5}
                fill="none"
                stroke="#00ff9d"
                strokeWidth={1.5}
                filter="url(#careerGlowMobile)"
              />
              <text
                x={branch.x + 16}
                y={branch.y + 4}
                fill="#e8eaf2"
                fontSize={12}
                fontWeight={500}
              >
                {branch.label}
              </text>
            </motion.g>
          ))}
        </motion.g>
      </svg>
    </div>
  );
}
